/**
 * Built-in database assembly.
 *
 * Group files are deliberately small and independent: adding profile 101 means
 * adding one entry to one group file (or a new group file), and nothing else in
 * the application changes (spec 84).
 */

import type { AmpProfile, CableProfile, DacProfile, HeadphoneProfile, SpeakerProfile } from '../types/hardware';
import { cableAffectsSignalPath, isCable } from '../types/hardware';
import { registry } from './registry';

import { DACS_00 } from '../profiles/dacs/group-00';
import { DACS_01 } from '../profiles/dacs/group-01';
import { DACS_02 } from '../profiles/dacs/group-02';
import { DACS_03 } from '../profiles/dacs/group-03';
import { DACS_04 } from '../profiles/dacs/group-04';
import { DACS_05 } from '../profiles/dacs/group-05';
import { DACS_06 } from '../profiles/dacs/group-06';
import { DACS_07 } from '../profiles/dacs/group-07';
import { DACS_08 } from '../profiles/dacs/group-08';
import { DACS_09 } from '../profiles/dacs/group-09';
import { DACS_10 } from '../profiles/dacs/group-10';
import { DACS_11 } from '../profiles/dacs/group-11';
import { DACS_12 } from '../profiles/dacs/group-12';

import { AMPS_00 } from '../profiles/amps/group-00';
import { AMPS_01 } from '../profiles/amps/group-01';
import { AMPS_02 } from '../profiles/amps/group-02';
import { AMPS_03 } from '../profiles/amps/group-03';
import { AMPS_04 } from '../profiles/amps/group-04';
import { AMPS_05 } from '../profiles/amps/group-05';
import { AMPS_06 } from '../profiles/amps/group-06';
import { AMPS_07 } from '../profiles/amps/group-07';
import { AMPS_08 } from '../profiles/amps/group-08';
import { AMPS_09 } from '../profiles/amps/group-09';
import { AMPS_10 } from '../profiles/amps/group-10';
import { AMPS_11 } from '../profiles/amps/group-11';
import { AMPS_12 } from '../profiles/amps/group-12';

import { SPEAKERS_00 } from '../profiles/speakers/group-00';
import { SPEAKERS_01 } from '../profiles/speakers/group-01';
import { SPEAKERS_02 } from '../profiles/speakers/group-02';
import { SPEAKERS_03 } from '../profiles/speakers/group-03';
import { SPEAKERS_04 } from '../profiles/speakers/group-04';
import { SPEAKERS_05 } from '../profiles/speakers/group-05';
import { SPEAKERS_06 } from '../profiles/speakers/group-06';
import { SPEAKERS_07 } from '../profiles/speakers/group-07';
import { SPEAKERS_08 } from '../profiles/speakers/group-08';
import { SPEAKERS_09 } from '../profiles/speakers/group-09';
import { SPEAKERS_10 } from '../profiles/speakers/group-10';
import { SPEAKERS_11 } from '../profiles/speakers/group-11';
import { SPEAKERS_12 } from '../profiles/speakers/group-12';

import { CABLES_00 } from '../profiles/cables/group-00';
import { CABLES_01 } from '../profiles/cables/group-01';
import { CABLES_02 } from '../profiles/cables/group-02';
import { CABLES_03 } from '../profiles/cables/group-03';
import { CABLES_04 } from '../profiles/cables/group-04';
import { CABLES_05 } from '../profiles/cables/group-05';
import { CABLES_06 } from '../profiles/cables/group-06';
import { CABLES_07 } from '../profiles/cables/group-07';
import { CABLES_08 } from '../profiles/cables/group-08';
import { CABLES_09 } from '../profiles/cables/group-09';
import { CABLES_10 } from '../profiles/cables/group-10';
import { CABLES_11 } from '../profiles/cables/group-11';
import { CABLES_12 } from '../profiles/cables/group-12';
import { CABLES_13 } from '../profiles/cables/group-13';

import { AMPS_ELECTROSTATIC } from '../profiles/amps/electrostatic-pack';
import { HEADPHONES_00 } from '../profiles/headphones/stax-sr-l700mk2';
import { CUSTOM_EXAMPLES } from '../profiles/custom/example';

export const ALL_DACS: DacProfile[] = [
  ...DACS_00, ...DACS_01, ...DACS_02, ...DACS_03, ...DACS_04, ...DACS_05, ...DACS_06,
  ...DACS_07, ...DACS_08, ...DACS_09, ...DACS_10, ...DACS_11, ...DACS_12,
];

export const ALL_AMPS: AmpProfile[] = [
  ...AMPS_00, ...AMPS_01, ...AMPS_02, ...AMPS_03, ...AMPS_04, ...AMPS_05, ...AMPS_06,
  ...AMPS_07, ...AMPS_08, ...AMPS_09, ...AMPS_10, ...AMPS_11, ...AMPS_12,
];

export const ALL_SPEAKERS: SpeakerProfile[] = [
  ...SPEAKERS_00, ...SPEAKERS_01, ...SPEAKERS_02, ...SPEAKERS_03, ...SPEAKERS_04, ...SPEAKERS_05,
  ...SPEAKERS_06, ...SPEAKERS_07, ...SPEAKERS_08, ...SPEAKERS_09, ...SPEAKERS_10, ...SPEAKERS_11,
  ...SPEAKERS_12,
];

export const ALL_HEADPHONES: HeadphoneProfile[] = [...HEADPHONES_00];

/**
 * Cables (spec 9 extension).
 *
 * Unlike the three source-chain categories, this list is deliberately many
 * models per manufacturer -- a brand's catalogue IS the useful unit here,
 * because the interesting comparison is usually within a range rather than
 * across brands. The one-model-per-manufacturer rule therefore does not apply,
 * and neither does the $1,000 floor.
 */
export const ALL_CABLES: CableProfile[] = [
  ...CABLES_00, ...CABLES_01, ...CABLES_02, ...CABLES_03, ...CABLES_04, ...CABLES_05, ...CABLES_06, ...CABLES_07, ...CABLES_08, ...CABLES_09, ...CABLES_10, ...CABLES_11, ...CABLES_12, ...CABLES_13,
];

/**
 * Extension packs sit alongside the core database. They are registered into the
 * same registry and are identical to the DSP, but they are NOT counted against
 * the one-model-per-manufacturer rule that governs the core 100 per category
 * (spec 9/10/11) -- that rule describes the initial database, and the registry
 * exists precisely so that it can be extended without renegotiating it.
 */
export const EXTENSION_AMPS: AmpProfile[] = [...AMPS_ELECTROSTATIC];

let loaded = false;

export function loadBuiltInDatabase(): void {
  if (loaded) return;
  registry.register(ALL_DACS, false);
  registry.register(ALL_AMPS, false);
  registry.register(EXTENSION_AMPS, false);
  registry.register(ALL_SPEAKERS, false);
  registry.register(ALL_HEADPHONES, false);
  registry.register(ALL_CABLES, false);
  registry.register(CUSTOM_EXAMPLES, true);
  loaded = true;
}

/** Spec-mandated default chain (spec 62). */
export const DEFAULT_SYSTEM = {
  dacId: 'dac.topping.d90-ak4499',
  ampId: 'amp.woo-audio.ges',
  outputId: 'hp.stax.sr-l700mk2',
};

export interface IntegrityReport {
  counts: { dac: number; amp: number; speaker: number; headphone: number; cable: number; total: number };
  duplicateManufacturers: Array<{ category: string; manufacturer: string; ids: string[] }>;
  duplicateIds: string[];
  belowPriceFloor: Array<{ id: string; label: string; amount: number; status: string }>;
  claimedMeasured: string[];
  missingProvenance: string[];
  /** Cables whose prose overstates what the engine actually computes. */
  cableClaims: string[];
  defaultsPresent: { dac: boolean; amp: boolean; output: boolean };
}

/**
 * Database integrity check (spec 81/82). Surfaced in the UI rather than only
 * run at build time, because user-imported profiles can violate the same rules.
 */
export function checkIntegrity(): IntegrityReport {
  const all = registry.all();
  const seen = new Map<string, number>();
  for (const p of all) seen.set(p.id, (seen.get(p.id) ?? 0) + 1);

  const dupMfr: IntegrityReport['duplicateManufacturers'] = [];
  for (const cat of ['dac', 'amp', 'speaker', 'headphone'] as const) {
    for (const d of registry.duplicateManufacturers(cat)) {
      dupMfr.push({ category: cat, manufacturer: d.manufacturer, ids: d.ids });
    }
  }

  const FX: Record<string, number> = { USD: 1, EUR: 1.08, GBP: 1.27, JPY: 0.0067 };
  const below: IntegrityReport['belowPriceFloor'] = [];
  const measured: string[] = [];
  const noProv: string[] = [];
  for (const p of all) {
    if (p.userDefined || p.isExample) continue;
    // The >$1,000 floor (spec 9/10/11) is a rule about the three SOURCE-CHAIN
    // categories, and it exists to keep the database aimed at hardware whose
    // behaviour is worth modelling. It was never a rule about cables, and
    // applying it to them would be actively wrong: a large part of what the
    // cable database is FOR is letting a listener put a $40 pro-audio cable
    // next to a $4,000 boutique one and see the engine compute the same
    // deviation for both. Excluding the cheap cable would quietly remove the
    // most useful comparison in the set.
    if (isCable(p)) continue;
    const usd = (p.price?.amount ?? 0) * (FX[p.price?.currency ?? 'USD'] ?? 1);
    if (usd < 1000) {
      below.push({ id: p.id, label: `${p.manufacturer} ${p.model}`, amount: usd, status: p.price?.status ?? 'UNKNOWN' });
    }
    if (p.confidence === 'MEASURED' && !p.measurement) measured.push(p.id);
    if (!p.provenanceNote) noProv.push(p.id);
  }

  // Cable-specific audit. Two failure modes matter and neither is caught by
  // the checks above: a cable whose notes make a claim the DSP does not back,
  // and an unmodelled type whose entry implies it does something.
  const cableClaims: string[] = [];
  const BANNED = /(warm(er|th)?|blacker|airier|liquid|musical|veil(ed)?|open(s|ed)? up|soundstage|holographic|smear|palpable|inky|analogue-?like)/i;
  for (const p of all) {
    if (!isCable(p) || p.userDefined || p.isExample) continue;
    const text = `${p.notes ?? ''} ${p.provenanceNote ?? ''}`;
    if (BANNED.test(text)) cableClaims.push(`${p.id}: subjective claim in prose`);
    else if (!cableAffectsSignalPath(p.cableType) && /dB/.test(p.notes ?? '')) {
      cableClaims.push(`${p.id}: quotes a dB figure for an unmodelled cable type`);
    }
  }

  return {
    cableClaims,
    counts: {
      dac: registry.countByCategory('dac'),
      amp: registry.countByCategory('amp'),
      speaker: registry.countByCategory('speaker'),
      headphone: registry.countByCategory('headphone'),
      cable: registry.countByCategory('cable'),
      total: all.length,
    },
    duplicateManufacturers: dupMfr,
    duplicateIds: [...seen].filter(([, n]) => n > 1).map(([id]) => id),
    belowPriceFloor: below,
    claimedMeasured: measured,
    missingProvenance: noProv,
    defaultsPresent: {
      dac: registry.has(DEFAULT_SYSTEM.dacId),
      amp: registry.has(DEFAULT_SYSTEM.ampId),
      output: registry.has(DEFAULT_SYSTEM.outputId),
    },
  };
}

export { registry };
